import { addDays, format } from 'date-fns';
import * as React from 'react';
import { Image, type ImageSourcePropType } from 'react-native';
import {
    BodyMediumEmphasized,
    BodyMediumRegular,
    Card,
    LabelRegular,
    Shadows,
    type StackProps,
    View,
    XStack,
    YStack,
} from 'smoothie';

import type { Guide } from '../../guidance/types/Guide';
import { useMembershipGuidanceSessionInfo } from '../hooks/useMembershipGuidanceSessionInfo';
import { GuidanceSessionType } from '../types/GuidanceSessions';

interface NoPastSessionsProps {
    guide?: Guide | null;
}

// Utility function to handle long titles
const truncateLongTitle = (title: string, maxLength: number = 20): string => {
    if (title.length <= maxLength) {
        return title;
    }

    // Split by spaces and use all but the last segment
    const words = title.split(' ');
    if (words.length <= 1) {
        return title;
    }

    // Remove the last word and join the rest
    const truncatedWords = words.slice(0, -1);
    return truncatedWords.join(' ');
};

const NoPastSessions = ({ guide }: NoPastSessionsProps) => {
    const { guidanceOnboardingOrder, getSessionDetails } =
        useMembershipGuidanceSessionInfo();
    const firstDummySession = getSessionDetails(
        guidanceOnboardingOrder[0] ?? GuidanceSessionType.QUICK_CHECK_IN,
    );
    const secondDummySession = getSessionDetails(
        guidanceOnboardingOrder[1] ?? GuidanceSessionType.QUICK_CHECK_IN,
    );

    return (
        <YStack marginTop={22} justifyContent="center" alignItems="center">
            <XStack alignItems="center">
                <DummyPastSessionCard
                    title={firstDummySession.title}
                    image={firstDummySession.image}
                    date={new Date()}
                    guide={guide}
                    zIndex={1}
                    rotate="-10deg"
                    marginTop={16}
                    marginBottom={27}
                    marginRight={-12}
                />
                <DummyPastSessionCard
                    title={secondDummySession.title}
                    image={secondDummySession.image}
                    date={addDays(new Date(), 7)}
                    guide={guide}
                    zIndex={2}
                    rotate="10deg"
                    marginTop={16}
                    marginBottom={27}
                    marginLeft={-12}
                />
            </XStack>

            <BodyMediumRegular
                width={300}
                marginTop={10}
                textAlign="center"
                alignSelf="center"
            >
                Your session history will appear here once you have completed
                your first session
            </BodyMediumRegular>
        </YStack>
    );
};

interface DummyPastSessionCardProps extends StackProps {
    title: string;
    image: ImageSourcePropType;
    date: Date;
    guide?: Guide | null;
}

const DummyPastSessionCard = ({
    title,
    image,
    date,
    guide,
    ...props
}: DummyPastSessionCardProps) => {
    const formattedSessionDate = format(date, 'MMM d yyyy');

    return (
        <Card
            backgroundColor="$white"
            borderWidth={1}
            borderColor="$gray200"
            borderRadius={8}
            padding={10}
            width={140}
            {...Shadows.SMALL}
            {...props}
        >
            <View
                width={70}
                height={70}
                justifyContent="center"
                alignItems="center"
                borderRadius={10}
                backgroundColor="$peach300"
            >
                <Image
                    source={image}
                    resizeMode="contain"
                    style={{
                        height: 48,
                    }}
                />
            </View>

            <BodyMediumEmphasized marginTop={12} marginBottom={4}>
                {truncateLongTitle(title)}
            </BodyMediumEmphasized>

            <LabelRegular color="$gray1000">
                {formattedSessionDate} &bull; {guide?.firstName ?? 'Durriya'}
            </LabelRegular>
        </Card>
    );
};

export default NoPastSessions;
